// =====================================================
// src/utils/deal-form-pdf.ts
// AJOUTÉ — export PDF du formulaire de soumission brut (rawFormData),
// distinct du CIM (cim-generation.service.ts), qui est un document
// narratif orienté acheteur, pas une reproduction fidèle du formulaire.
//
// CORRIGÉ — noms de champs alignés sur le vrai src/types/submit-deal.types.ts.
// Sections Impact & Main-d'œuvre et Documents & Confidentialité ajoutées.
// AJOUTÉ — section Technologie & Outils (formData.technology).
// =====================================================

import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { generatePdfFromHtml } from '@/utils/pdf-generator';
import { SUBMIT_DEAL_TRANSLATIONS } from '@/constants/submit-deal.translations';

type Lang = 'en' | 'fr';

function esc(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function money(v: unknown, currency = 'USD'): string {
  if (v === null || v === undefined || v === ('' as unknown)) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(n);
  } catch {
    return `$${n.toLocaleString()}`;
  }
}

function yesNo(v: unknown, lang: Lang): string {
  if (v === true) return lang === 'fr' ? 'Oui' : 'Yes';
  if (v === false) return lang === 'fr' ? 'Non' : 'No';
  return '—';
}

function section(title: string, rowsHtml: string): string {
  if (!rowsHtml.trim()) return '';
  return `
    <div class="section">
      <h2>${esc(title)}</h2>
      <table>${rowsHtml}</table>
    </div>`;
}

function row(label: string, value: string): string {
  return `<tr><td class="label">${esc(label)}</td><td class="value">${value}</td></tr>`;
}

function block(label: string, text: unknown): string {
  const v = text ? String(text).trim() : '';
  if (!v) return '';
  return `<div class="block"><div class="block-label">${esc(label)}</div><div class="block-text">${esc(v).replace(/\n/g, '<br/>')}</div></div>`;
}

export function renderSubmitDealFormToHtml(formData: any, meta: {
  referenceNumber?: string;
  submittedAt?: string;
  submitterRole?: string;
  language?: Lang;
} = {}): string {
  const lang: Lang = meta.language || 'fr';
  const t = SUBMIT_DEAL_TRANSLATIONS[lang] || SUBMIT_DEAL_TRANSLATIONS.en;
  const g = formData?.general || {};
  const op = formData?.operations || {};
  const lic = formData?.licensing || {};
  const tech = formData?.technology || {};
  const as = formData?.assets || {};
  const re = formData?.realEstate || {};
  const fin = formData?.financials || {};
  const ab = formData?.addbacks || {};
  const pr = formData?.projections || {};
  const cu = formData?.culture || {};
  const sw = formData?.swot || {};
  const doc11 = formData?.documents || {};
  const imp = formData?.impact || {};
  const currency = g.currency || 'USD';
  const businessName = g.tradeName || g.legalName || (lang === 'fr' ? 'Entreprise sans nom' : 'Unnamed business');

  const ownersRows = Array.isArray(g.owners) && g.owners.length > 0
    ? g.owners.map((o: any) =>
        `<li>${esc(o.name)} — ${esc(o.ownershipPercent)}% — ${esc(o.role)}${o.email ? ` — ${esc(o.email)}` : ''}${o.phone ? ` — ${esc(o.phone)}` : ''}</li>`
      ).join('')
    : '';

  const inventoryItemsRows = Array.isArray(as.inventoryItems) && as.inventoryItems.length > 0
    ? as.inventoryItems.map((i: any) =>
        `<li>${esc(i.description)} (${esc(i.category)}) — ${esc(i.quantity)} × ${money(i.unitValue, currency)}</li>`
      ).join('')
    : '';

  const assetDocsRows = Array.isArray(as.assetListDocuments) && as.assetListDocuments.length > 0
    ? as.assetListDocuments.map((f: any) => `<li>${esc(f.name)}</li>`).join('')
    : '';

  const realEstatePropsRows = Array.isArray(re.properties) && re.properties.length > 0
    ? re.properties.map((p: any) =>
        `<li>${esc(p.address)} — ${p.status === 'owned' ? (lang === 'fr' ? 'Propriété' : 'Owned') : (lang === 'fr' ? 'Loué' : 'Leased')} — ${money(p.estimatedValue, currency)}${p.status === 'leased' && p.monthlyRent ? ` — ${money(p.monthlyRent, currency)}/mo` : ''}</li>`
      ).join('')
    : '';

  const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #1D314D; font-size: 12px; line-height: 1.5; }
  h1 { color: #1D314D; font-size: 20px; margin-bottom: 2px; }
  .subtitle { color: #666; font-size: 12px; margin-bottom: 18px; }
  h2 { color: #1D314D; font-size: 14px; border-bottom: 2px solid #CEAB65; padding-bottom: 4px; margin-top: 22px; margin-bottom: 8px; }
  .meta { background: #F5F7FA; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 11px; color: #444; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  td { padding: 4px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
  td.label { color: #666; width: 40%; }
  td.value { color: #1D314D; font-weight: 600; }
  .block { margin: 8px 0; }
  .block-label { color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
  .block-text { color: #333; }
  ul { margin: 4px 0; padding-left: 18px; }
  .footer { margin-top: 30px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 8px; }
</style>
</head>
<body>
  <h1>${esc(businessName)}</h1>
  <p class="subtitle">${lang === 'fr' ? 'Formulaire de soumission — copie brute' : 'Submission form — raw copy'}</p>

  <div class="meta">
    ${meta.referenceNumber ? `${lang === 'fr' ? 'Référence' : 'Reference'}: <strong>${esc(meta.referenceNumber)}</strong><br/>` : ''}
    ${meta.submittedAt ? `${lang === 'fr' ? 'Soumis le' : 'Submitted on'}: ${esc(meta.submittedAt)}<br/>` : ''}
    ${meta.submitterRole ? `${lang === 'fr' ? 'Soumis par' : 'Submitted as'}: ${esc(meta.submitterRole)}` : ''}
  </div>

  ${section(t.steps.step1, [
    row(t.step1.legalName, esc(g.legalName)),
    row(t.step1.tradeName, esc(g.tradeName)),
    row(t.step1.entityType, esc(g.entityType)),
    row(lang === 'fr' ? 'Site web' : 'Website', esc(g.website)),
    row(lang === 'fr' ? 'Devise' : 'Currency', esc(currency)),
    row(t.step1.industry, esc(g.industry)),
    row(t.step1.naicsCode, esc(g.naicsCode)),
    row(`${t.step1.city} / ${t.step1.state} / ${t.step1.country}`, `${esc(g.locationCity)}, ${esc(g.locationState)}, ${esc(g.locationCountry)}`),
    row(t.step1.yearEstablished, esc(g.yearEstablished)),
    row(t.step1.previouslyListed, yesNo(g.previouslyListed, lang)),
    row(t.step1.ownershipCount, esc(g.ownershipCount)),
    row(t.step1.primaryOwnerPercent, g.primaryOwnerPercent != null ? `${esc(g.primaryOwnerPercent)}%` : '—'),
  ].join(''))}
  ${ownersRows ? `<div class="block"><div class="block-label">${lang === 'fr' ? 'Détail des propriétaires' : 'Ownership detail'}</div><ul>${ownersRows}</ul></div>` : ''}
  ${block(t.step1.dealTitle, g.title)}
  ${block(t.step1.description, g.description)}
  ${block(t.step1.reasonForSale, g.reasonForSale)}

  ${section(t.steps.step2, [
    row(t.step2.fullTimeEmployees, esc(op.fullTimeEmployees)),
    row(t.step2.partTimeEmployees, esc(op.partTimeEmployees)),
    row(lang === 'fr' ? 'Sous-traitants' : 'Subcontractors', esc(op.subcontractors)),
    row(t.step2.customerConcentration, op.customerConcentration != null ? `${esc(op.customerConcentration)}%` : '—'),
    row(t.step2.ownerHoursPerWeek, esc(op.ownerHoursPerWeek)),
    row(t.step2.ownerType, esc(op.ownerType)),
    row(lang === 'fr' ? "Salaire annuel du propriétaire" : "Owner's annual salary", money(op.ownerAnnualSalary, currency)),
    row(lang === 'fr' ? "Taux de roulement du personnel" : 'Employee turnover rate', op.employeeTurnoverRate != null ? `${esc(op.employeeTurnoverRate)}%` : '—'),
    row(lang === 'fr' ? 'Personnel syndiqué' : 'Unionized staff', yesNo(op.unionAffiliated, lang)),
    row(lang === 'fr' ? 'Langues parlées' : 'Languages spoken', esc(op.languages)),
  ].join(''))}
  ${block(t.step2.dailyOperationsDescription, op.dailyOperationsDescription)}
  ${block(lang === 'fr' ? 'Fournisseurs clés' : 'Key suppliers', op.keySuppliers)}
  ${block(t.step2.ownerCriticalTasks, op.ownerCriticalTasks)}
  ${block(t.step2.ownerAbsenceImpact, op.ownerAbsenceImpact)}
  ${block(lang === 'fr' ? 'Détails syndicaux' : 'Union details', op.unionDetails)}

  ${section(t.steps.step3, [
    row(t.step3.specialLicensesRequired, yesNo(lic.specialLicensesRequired, lang)),
  ].join(''))}
  ${block(t.step3.licenseDetails, lic.licenseDetails)}
  ${block(t.step3.regulatoryRequirements, lic.regulatoryRequirements)}
  ${block(t.step3.legalDisputes, lic.legalDisputes)}

  ${section(t.step3.technologySectionTitle, [
    row(t.step3.hasProprietaryTech, yesNo(tech.hasProprietaryTech, lang)),
    row(t.step3.toolsTransferable, yesNo(tech.toolsTransferable, lang)),
    row(t.step3.hostingProvider, esc(tech.hostingProvider)),
  ].join(''))}
  ${block(t.step3.proprietaryTechDescription, tech.proprietaryTechDescription)}
  ${block(t.step3.techStackDescription, tech.techStackDescription)}
  ${block(t.step3.thirdPartyTools, tech.thirdPartyTools)}
  ${block(t.step3.requiredBuyerTechSkills, tech.requiredBuyerTechSkills)}

  ${section(t.steps.step4, [
    row(t.step4.inventoryValue, money(as.inventoryValue, currency)),
    row(t.step4.ffeValue, money(as.ffeValue, currency)),
    row(t.step4.accountsReceivable, money(as.accountsReceivable, currency)),
    row(t.step4.otherCurrentAssets, money(as.otherCurrentAssets, currency)),
    row(t.step4.patentsOrTrademarks, yesNo(as.patentsOrTrademarks, lang)),
  ].join(''))}
  ${inventoryItemsRows ? `<div class="block"><div class="block-label">${lang === 'fr' ? "Détail de l'inventaire" : 'Inventory detail'}</div><ul>${inventoryItemsRows}</ul></div>` : ''}
  ${block(t.step4.intellectualPropertyDetails, as.intellectualPropertyDetails)}
  ${assetDocsRows ? `<div class="block"><div class="block-label">${lang === 'fr' ? "Documents d'actifs joints" : 'Attached asset documents'}</div><ul>${assetDocsRows}</ul></div>` : ''}

  ${re.isIncluded !== undefined ? section(t.steps.step5, [
    row(t.step5.isIncluded, yesNo(re.isIncluded, lang)),
    row(t.step5.realEstateAskPrice, money(re.realEstateAskPrice, currency)),
    row(t.step5.monthlyRent, money(re.monthlyRent, currency)),
    row(t.step5.leaseExpirationDate, esc(re.leaseExpirationDate)),
  ].join('')) : ''}
  ${realEstatePropsRows ? `<div class="block"><div class="block-label">${lang === 'fr' ? 'Propriétés' : 'Properties'}</div><ul>${realEstatePropsRows}</ul></div>` : ''}

  ${section(t.steps.step6, [
    row(t.step6.askingPrice, money(fin.askingPrice, currency)),
    row(lang === 'fr' ? "Immobilier inclus dans le prix" : 'Real estate included in price', yesNo(fin.includeRealEstateInPrice, lang)),
    fin.includeRealEstateInPrice ? row(lang === 'fr' ? 'Prix total (entreprise + immobilier)' : 'Total price (business + real estate)', money(fin.totalAskingPriceWithRealEstate, currency)) : '',
    row(`${t.step6.revenueYear1} / ${t.step6.revenueYear2} / ${t.step6.revenueYear3}`, `${money(fin.revenueYear1, currency)} / ${money(fin.revenueYear2, currency)} / ${money(fin.revenueYear3, currency)}`),
    row(`${t.step6.ebitdaYear1} / ${t.step6.ebitdaYear2} / ${t.step6.ebitdaYear3}`, `${money(fin.ebitdaYear1, currency)} / ${money(fin.ebitdaYear2, currency)} / ${money(fin.ebitdaYear3, currency)}`),
    row(t.step6.cashFlowEquity, money(fin.cashFlowEquity, currency)),
    row(t.step6.totalLiabilities, money(fin.totalLiabilities, currency)),
    row(t.step6.inheritedDebt, money(fin.inheritedDebt, currency)),
  ].join(''))}

  ${section(t.steps.step7, [
    row(t.step7.ownerSalary, money(ab.ownerSalary, currency)),
    row(t.step7.autoExpense, money(ab.autoExpense, currency)),
    row(t.step7.travelEntertainment, money(ab.travelEntertainment, currency)),
    row(t.step7.nonRecurringLegal, money(ab.nonRecurringLegal, currency)),
    row(t.step7.otherDiscretionary, money(ab.otherDiscretionary, currency)),
  ].join(''))}
  ${block(t.step7.addbackRationale, ab.addbackRationale)}

  ${section(t.steps.step8, [
    row(`${lang === 'fr' ? 'CA projeté (An 1/2/3)' : 'Projected revenue (Yr 1/2/3)'}`, `${money(pr.revenueYear1, currency)} / ${money(pr.revenueYear2, currency)} / ${money(pr.revenueYear3, currency)}`),
    row(`${lang === 'fr' ? 'EBITDA projeté (An 1/2/3)' : 'Projected EBITDA (Yr 1/2/3)'}`, `${money(pr.ebitdaYear1, currency)} / ${money(pr.ebitdaYear2, currency)} / ${money(pr.ebitdaYear3, currency)}`),
    row(t.step8.revenueGrowthForecast, pr.revenueGrowthForecast != null ? `${esc(pr.revenueGrowthForecast)}%` : '—'),
    row(t.step8.ebitdaGrowthForecast, pr.ebitdaGrowthForecast != null ? `${esc(pr.ebitdaGrowthForecast)}%` : '—'),
    row(t.step8.sellerFinancingOffered, yesNo(pr.sellerFinancingOffered, lang)),
    row(t.step8.sfAmount, money(pr.sellerFinancingAmount, currency)),
    row(t.step8.sfPercentage, pr.sellerFinancingPercentage != null ? `${esc(pr.sellerFinancingPercentage)}%` : '—'),
    row(t.step8.sfInterestRate, pr.sellerFinancingInterestRate != null ? `${esc(pr.sellerFinancingInterestRate)}%` : '—'),
    row(t.step8.sfTermMonths, esc(pr.sellerFinancingTermMonths)),
    row(t.step8.sfDownPayment, pr.sellerFinancingDownPaymentPercent != null ? `${esc(pr.sellerFinancingDownPaymentPercent)}%` : '—'),
    row(t.step8.sfStandby, esc(pr.sellerFinancingStandbyPeriodMonths)),
    row(t.step8.sfCollateral, esc(pr.sellerFinancingCollateralType)),
  ].join(''))}
  ${block(t.step8.growthOpportunities, pr.growthOpportunities)}
  ${block(t.step8.sellerFinancingDetails, pr.sellerFinancingDetails)}
  ${block(t.step8.sfNotes, pr.sellerFinancingNotes)}

  ${section(t.steps.step9, [
    row(t.step9.managementTeam, esc(cu.managementTeam)),
    row(t.step9.transitionDuration, esc(cu.transitionDurationPreference)),
    row(t.step9.nonCompetePeriod, esc(cu.nonCompetePeriod)),
    row(lang === 'fr' ? 'Rayon de non-concurrence' : 'Non-compete radius', cu.nonCompeteRadius ? `${esc(cu.nonCompeteRadius)} ${esc(cu.nonCompeteRadiusUnit)}` : '—'),
  ].join(''))}
  ${Array.isArray(cu.coreValues) && cu.coreValues.filter((v: string) => v).length > 0 ? `<div class="block"><div class="block-label">${t.step9.coreValues}</div><ul>${cu.coreValues.map((v: string, i: number) => v ? `<li>${esc(v)}${cu.coreValueDescriptions?.[i] ? `: ${esc(cu.coreValueDescriptions[i])}` : ''}</li>` : '').join('')}</ul></div>` : ''}
  ${block(t.step9.managementTeamExplanation, cu.managementTeamExplanation)}
  ${block(t.step9.idealSuccessor, cu.idealSuccessorDescription)}
  ${block(lang === 'fr' ? 'Disponibilité pour consultation post-vente' : 'Post-sale consulting availability', cu.postSaleConsultingDetails)}
  ${block(t.step9.biggestFear, cu.biggestFearRegardingSale)}
  ${block(t.step9.dealBreakers, cu.dealBreakersFromSeller)}
  ${block(lang === 'fr' ? 'Défis actuels' : 'Current challenges', cu.currentChallenges)}
  ${block(lang === 'fr' ? 'Objectifs de croissance' : 'Growth goals', cu.growthGoals)}

  ${section(t.steps.step10, '')}
  ${block(t.step10.strengths, sw.strengths)}
  ${block(t.step10.weaknesses, sw.weaknesses)}
  ${block(t.step10.opportunities, sw.opportunities)}
  ${block(t.step10.threats, sw.threats)}

  ${section(lang === 'fr' ? 'Impact & Main-d\u2019œuvre' : 'Impact & Workforce', [
    row(lang === 'fr' ? "Nombre d'employés" : 'Number of employees', esc(imp.employeeCount)),
    row(lang === 'fr' ? "Ancienneté moyenne (années)" : 'Average tenure (years)', esc(imp.averageEmployeeTenureYears)),
    row(lang === 'fr' ? 'Régime de retraite offert' : 'Retirement plan offered', yesNo(imp.hasRetirementPlan, lang)),
    row(lang === 'fr' ? 'Accidents de travail (3 dernières années)' : 'Workplace accidents (last 3 years)', esc(imp.workplaceAccidentsLast3yr)),
    row(lang === 'fr' ? "Chaîne d'approvisionnement locale" : 'Local supply chain', imp.localSupplyChainPercent != null ? `${esc(imp.localSupplyChainPercent)}%` : '—'),
    row(lang === 'fr' ? 'Certification environnementale' : 'Environmental certification', yesNo(imp.hasEnvironmentalCertification, lang)),
    row(lang === 'fr' ? 'Historique de litiges' : 'Litigation history', yesNo(imp.hasLitigationHistory, lang)),
    row(lang === 'fr' ? 'Implication communautaire' : 'Community involvement', yesNo(imp.communityInvolvement, lang)),
  ].join(''))}

  ${section(t.step11?.sectionTitle || (lang === 'fr' ? 'Documents et Confidentialité' : 'Documents & Confidentiality'), [
    row(t.step11?.confidentialityLevel || (lang === 'fr' ? 'Niveau de confidentialité' : 'Confidentiality level'), esc(doc11.confidentialityLevel)),
    row(t.step11?.lendersShareConsent || (lang === 'fr' ? 'Partage avec les prêteurs autorisé' : 'Lender sharing authorized'), yesNo(doc11.lendersShareConsent, lang)),
    row(t.step11?.hideLegalName || (lang === 'fr' ? 'Raison sociale masquée' : 'Legal name hidden'), yesNo(doc11.publicAnonymizationFlags?.hideLegalName, lang)),
    row(t.step11?.hideExactLocation || (lang === 'fr' ? 'Emplacement exact masqué' : 'Exact location hidden'), yesNo(doc11.publicAnonymizationFlags?.hideExactLocation, lang)),
    row(t.step11?.hideOwnerName || (lang === 'fr' ? 'Nom du propriétaire masqué' : 'Owner name hidden'), yesNo(doc11.publicAnonymizationFlags?.hideOwnerName, lang)),
  ].join(''))}

  <div class="footer">
    ${lang === 'fr'
      ? "Ce document est une copie brute du formulaire tel que soumis — il ne remplace pas le Mémorandum d'Information Confidentiel (CIM) préparé par l'équipe AcquizGroup."
      : "This document is a raw copy of the submitted form — it does not replace the Confidential Information Memorandum (CIM) prepared by the AcquizGroup team."}
  </div>
</body>
</html>`;

  return html;
}

export async function downloadDealFormPdf(dealId: string, language: Lang = 'fr'): Promise<void> {
  const snap = await getDoc(doc(db, 'dealSubmissions', dealId));
  if (!snap.exists()) {
    throw new Error(language === 'fr' ? 'Dossier introuvable.' : 'Deal not found.');
  }
  const data = snap.data() as any;
  const formData = data.rawFormData;

  const meta = {
    referenceNumber: data.referenceNumber,
    submittedAt: data.submittedAt || data.createdAt?.toDate?.()?.toLocaleDateString(),
    submitterRole: data.submitterRole,
    language,
  };

  let html: string;
  if (formData) {
    html = renderSubmitDealFormToHtml(formData, meta);
  } else {
    const reconstructed = {
      general: {
        legalName: data.legalName, tradeName: data.businessName, entityType: data.entityType,
        title: data.title, description: data.description, industry: data.industry,
        locationCity: data.locationCity, locationCountry: data.locationCountry,
        yearEstablished: data.yearEstablished, currency: data.currency,
      },
      financials: { askingPrice: data.askingPrice },
    };
    html = renderSubmitDealFormToHtml(reconstructed, meta) +
      `<p style="color:#b45309;font-size:11px;margin-top:10px;">${
        language === 'fr'
          ? "⚠ Formulaire complet indisponible pour ce dossier (soumis via une ancienne version du système) — seules les informations de base sont affichées ci-dessus."
          : '⚠ Full form unavailable for this file (submitted via an older version of the system) — only basic information is shown above.'
      }</p>`;
  }

  const businessName = formData?.general?.tradeName || formData?.general?.legalName || data.businessName || 'deal';
  const safeName = String(businessName).replace(/[^a-z0-9]+/gi, '_').slice(0, 60);

  await generatePdfFromHtml(html, {
    filename: `${safeName}_formulaire_${data.referenceNumber || dealId}.pdf`,
  });
}